import { BILLING_CYCLE, BASE_PLAN_SUB_TYPES } from '@/common/constants';
import { mapGetters } from 'vuex';
import moment from 'moment';
import { LocalDateFormat } from '@/utils/dateformat';

/**
 * Mixin for Pro annual billed monthly to monthly downgrade scenarios
 * Provides computed properties to check if current scenario matches the downgrade pattern
 */
export default {
  computed: {
    ...mapGetters(['currentEditPlan', 'getSelectedPlans', 'getSubscriptions', 'nextChargeDate']),

    /**
     * Check if current edit plan is Pro annual billed monthly
     * @returns {boolean} True if current edit plan is Pro with annual term monthly billing
     */
    currentEditPlanIsProAnnualBilledMonthly() {
      return (
        this.currentEditPlan?.category === BASE_PLAN_SUB_TYPES.PRO &&
        this.currentEditPlan?.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY
      );
    },

    currentEditPlanIsBiz() {
      return (
        this.currentEditPlan?.category === BASE_PLAN_SUB_TYPES.BIZ
      );
    },

    currentEditPlanIsProMonthly() {
      return (
        this.currentEditPlan?.category === BASE_PLAN_SUB_TYPES.PRO &&
        this.currentEditPlan?.billing_cycle === BILLING_CYCLE.MONTHLY
      );
    },

    /**
     * Check if selected plans only contains one Pro monthly plan
     * @returns {boolean} True if there's exactly one Pro monthly plan selected
     */
    currentSelectedPlansOnlyHasProMonthly() {
      return (
        this.getSelectedPlans.length === 1 &&
        this.getSelectedPlans[0]?.category === BASE_PLAN_SUB_TYPES.PRO &&
        this.getSelectedPlans[0]?.billing_cycle === BILLING_CYCLE.MONTHLY
      );
    },

    currentSelectedPlansOnlyHasProAnnualBilledMonthly() {
      return (
        this.getSelectedPlans.length === 1 &&
        this.getSelectedPlans[0]?.category === BASE_PLAN_SUB_TYPES.PRO &&
        this.getSelectedPlans[0]?.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY
      );
    },

    /**
     * Check if current scenario is Pro annual billed monthly to monthly downgrade
     * @returns {boolean} True if it's edit flow with Pro annual billed monthly to monthly downgrade
     */
    isProAnnualBilledMonthlyToMonthlyDowngrade() {
      return (
        this.$context?.is?.edit &&
        this.currentEditPlanIsProAnnualBilledMonthly &&
        this.currentSelectedPlansOnlyHasProMonthly
      );
    },
    isProAnnualBilledMonthlyDecrease() {
      return (
        this.$context?.is?.edit &&
        this.currentEditPlanIsProAnnualBilledMonthly &&
        this.currentSelectedPlansOnlyHasProAnnualBilledMonthly &&
        this.getSelectedPlans[0]?.cartInfo?.selected_number < this.currentEditPlan?.cartInfo?.selected_number
      );
    },
    isBizDowngradeToProAnnualBilledMonthly() {
      return (
        this.$context?.is?.edit &&
        this.currentEditPlanIsBiz &&
        this.currentSelectedPlansOnlyHasProAnnualBilledMonthly
      );
    },
    isProMonthlyToAnnualBilledMonthlyUpgrade() {
      return (
        this.$context?.is?.edit &&
        this.currentEditPlanIsProMonthly &&
        this.currentSelectedPlansOnlyHasProAnnualBilledMonthly
      ) || (
        this.$context?.is?.add2cart &&
        this.getSelectedPlans[0]?.category === BASE_PLAN_SUB_TYPES.PRO &&
        this.getSelectedPlans[0]?.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY
      );
    },
    getProAnnualBilledMonthlyExpireTime() {
      return (this.isProAnnualBilledMonthlyToMonthlyDowngrade || this.isProAnnualBilledMonthlyDecrease) ? this.getSubscriptions.find(subscription => subscription.plan_code === this.currentEditPlan?.plan_code)?.planExpireTime ?? '' : '';
    },
    getProAnnualBilledMonthlyExpireFormatDate() {
      return this.getProAnnualBilledMonthlyExpireTime ? moment(this.getProAnnualBilledMonthlyExpireTime).format(LocalDateFormat) : '';
    },
    getProAnnualBilledMonthlyNextChargeDate() {
      if (this.isProMonthlyToAnnualBilledMonthlyUpgrade) {
        return this.nextChargeDate?.nextMonthlyDate ? moment(this.nextChargeDate?.nextMonthlyDate).format(LocalDateFormat) : '';
      }
      const proAnnualBilledMonthly = this.currentEditPlanIsProAnnualBilledMonthly ? this.getSubscriptions.find(subscription => subscription.plan_code === this.currentEditPlan?.plan_code) : null;
      if (!proAnnualBilledMonthly) return '';
      return moment(proAnnualBilledMonthly.currentPaymentMonthEndDate).format(LocalDateFormat);
    },
    getProAnnualBilledMonthlyNextRenewalDate() {
      // ! note: nextAnnualDate is the next renewal date of the annual billed monthly plan
      // ! The interim solution, need restructuring in the future.
      // * default to next year if no next annual date
      return (this.isProMonthlyToAnnualBilledMonthlyUpgrade && this.nextChargeDate?.nextAnnualDate) ?
        moment(this.nextChargeDate?.nextAnnualDate).format(LocalDateFormat) :
        moment(Date.now())
          .add(1, 'year')
          .format(LocalDateFormat);
    },
  },
};
